import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { supabase } from "../lib/supabase";
import { useUser } from "../context/UserContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AboutAccountModal from "../components/AboutAccountModal";
import SharePostModal from "../components/SharePostModal";
import {
    faUser,
    faHeart as faHeartRegular,
    faComment,
    faBookmark,
    faPaperPlane,
} from "@fortawesome/free-regular-svg-icons";
import {
    faHeart as faHeartSolid,
    faEllipsisVertical,
    faChevronUp,
    faChevronDown,
    faTimes,
    faPaperPlane as faPaperPlaneSolid,
    faBookmark as faBookmarkSolid,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/postsPage.css";

function timeAgo(dateStr) {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    if (diff < 2592000) return `${Math.floor(diff / 604800)}w`;
    if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo`;
    return `${Math.floor(diff / 31536000)}y`;
}

export default function PostsPage() {
    const { user, profile } = useUser();
    const navigate = useNavigate();
    const { postId: targetPostId } = useParams(); // support /posts/:postId direct links

    const [posts, setPosts] = useState([]);
    const [index, setIndex] = useState(0);
    const [isFetching, setIsFetching] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const didJumpToTarget = useRef(false); // only jump once on direct-link load

    const [slots, setSlots] = useState({ a: 0, b: null, dir: null });
    const isAnimating = useRef(false);

    // Comments
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [submittingComment, setSubmittingComment] = useState(false);
    const commentInputRef = useRef(null);
    // Per-post comment counts  { [postId]: number }
    const [commentCounts, setCommentCounts] = useState({});

    // Dots / About
    const [showDots, setShowDots] = useState(false);
    const [showAboutAccount, setShowAboutAccount] = useState(false);
    const [fullAboutProfile, setFullAboutProfile] = useState(null); // full profile with country+created_at
    const [copiedMsg, setCopiedMsg] = useState(false);

    // Bookmarks: { [postId]: boolean }
    const [bookmarks, setBookmarks] = useState({});
    // Share modal
    const [showShare, setShowShare] = useState(false);

    // Touch
    const containerRef = useRef(null);
    const isFetchingRef = useRef(false);

    // ─── FETCH (ranked feed) ──────────────────────────────────────────────────
    // Fetch BATCH_SIZE posts, score each by likes + recency decay, sort descending.
    // Score = likesCount + 10 * exp(-ageDays / 7)
    // → Recent posts get a strong initial boost that decays over ~7 days.
    // → Popular older posts still beat unpopular new ones once the boost fades.
    const BATCH_SIZE = 20;
    const seenPostIds = useRef(new Set());
    const fetchedPages = useRef(0);

    const fetchPosts = useCallback(async () => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        setIsFetching(true);

        const pageNum = fetchedPages.current;
        const { data, error } = await supabase
            .from("posts")
            .select(`
                id, caption, created_at, hide_like_counts, turn_off_commenting,
                profiles:user_id(id, username, avatar_url),
                post_images(id, image_path, order_index),
                likes(user_id)
            `)
            .range(pageNum * BATCH_SIZE, pageNum * BATCH_SIZE + BATCH_SIZE - 1)
            .order("created_at", { ascending: false });

        if (error) { isFetchingRef.current = false; setIsFetching(false); return; }
        if (data.length < BATCH_SIZE) setHasMore(false);
        fetchedPages.current += 1;

        // De-dupe
        const fresh = data.filter(p => !seenPostIds.current.has(p.id));
        fresh.forEach(p => seenPostIds.current.add(p.id));

        // Score & sort
        const now = Date.now();
        const scored = fresh
            .map(post => {
                const likesCount = post.likes?.length || 0;
                const ageDays = (now - new Date(post.created_at).getTime()) / 86_400_000;
                return { ...post, _score: likesCount + 10 * Math.exp(-ageDays / 7) };
            })
            .sort((a, b) => b._score - a._score);

        // Signed URLs
        const formatted = await Promise.all(scored.map(async (post) => {
            const sorted = (post.post_images || []).sort((a, b) => a.order_index - b.order_index);
            const withUrls = await Promise.all(sorted.map(async (img) => {
                const { data: sd } = await supabase.storage
                    .from("posts").createSignedUrl(img.image_path, 3600);
                return { ...img, signedUrl: sd?.signedUrl || null };
            }));
            return {
                ...post,
                post_images: withUrls,
                likesCount: post.likes?.length || 0,
                isLiked: post.likes?.some(l => l.user_id === user?.id) || false,
            };
        }));

        setPosts(prev => [...prev, ...formatted]);

        // Comment counts
        const postIds = formatted.map(p => p.id);
        if (postIds.length > 0) {
            const { data: countData } = await supabase
                .from("comments").select("post_id").in("post_id", postIds);
            if (countData) {
                const counts = {};
                countData.forEach(row => { counts[row.post_id] = (counts[row.post_id] || 0) + 1; });
                setCommentCounts(prev => ({ ...prev, ...counts }));
            }

            // Bookmark status for this batch
            if (user) {
                const { data: bmData } = await supabase
                    .from("bookmarks")
                    .select("post_id")
                    .eq("user_id", user.id)
                    .in("post_id", postIds);
                if (bmData) {
                    const bm = {};
                    bmData.forEach(row => { bm[row.post_id] = true; });
                    setBookmarks(prev => ({ ...prev, ...bm }));
                }
            }
        }

        isFetchingRef.current = false;
        setIsFetching(false);
    }, [user]);

    useEffect(() => { fetchPosts(); }, []);

    useEffect(() => {
    document.title = "Posts • CodeGram";
  }, []);

    // Jump to targetPostId from direct link
    useEffect(() => {
        if (!targetPostId || didJumpToTarget.current || posts.length === 0) return;
        const targetIdx = posts.findIndex(p => p.id === targetPostId);
        if (targetIdx !== -1) {
            didJumpToTarget.current = true;
            setIndex(targetIdx);
            setSlots({ a: targetIdx, b: null, dir: null });
        } else if (hasMore) {
            fetchPosts();
        }
    }, [posts, targetPostId, hasMore]);

    // Pre-fetch next batch when 3 posts from end
    useEffect(() => {
        if (posts.length > 0 && index >= posts.length - 3 && hasMore && !isFetchingRef.current) {
            fetchPosts();
        }
    }, [index, posts.length, hasMore]);

    // Sync URL
    useEffect(() => {
        const post = posts[index];
        if (post) window.history.replaceState(null, "", `/posts/${post.id}`);
    }, [index, posts]);


    // ─── NAVIGATION ───────────────────────────────────────────────────────────
    // Keep live values in refs so goNext/goPrev never need to be recreated.
    // This means keyboard/wheel listeners are registered ONCE and never hold stale closures.
    const indexRef = useRef(0);
    const postsLengthRef = useRef(0);
    const showCommentsRef = useRef(false);

    useEffect(() => { indexRef.current = index; }, [index]);
    useEffect(() => { postsLengthRef.current = posts.length; }, [posts.length]);
    useEffect(() => { showCommentsRef.current = showComments; }, [showComments]);

    // Stable navigate functions — created once, never change
    const goNext = useCallback(() => {
        if (isAnimating.current) return;
        const idx = indexRef.current;
        const len = postsLengthRef.current;
        if (idx >= len - 1) return;
        isAnimating.current = true;
        const nextIdx = idx + 1;
        setSlots({ a: idx, b: nextIdx, dir: "up" });
        setTimeout(() => {
            setIndex(nextIdx);
            setSlots({ a: nextIdx, b: null, dir: null });
            setShowComments(false);
            setShowDots(false);
            isAnimating.current = false;
        }, 370);
    }, []); // no deps — reads from refs

    const goPrev = useCallback(() => {
        if (isAnimating.current) return;
        const idx = indexRef.current;
        if (idx === 0) return;
        isAnimating.current = true;
        const prevIdx = idx - 1;
        setSlots({ a: idx, b: prevIdx, dir: "down" });
        setTimeout(() => {
            setIndex(prevIdx);
            setSlots({ a: prevIdx, b: null, dir: null });
            setShowComments(false);
            setShowDots(false);
            isAnimating.current = false;
        }, 370);
    }, []); // no deps — reads from refs

    // ── Keyboard (registered once, stable) ──
    useEffect(() => {
        const handler = (e) => {
            if (showCommentsRef.current) return;
            if (e.key === "ArrowDown") { e.preventDefault(); goNext(); }
            if (e.key === "ArrowUp")   { e.preventDefault(); goPrev(); }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []); // stable — goNext/goPrev never change, ref tracks showComments

    // ── Trackpad / mouse-wheel (registered once, stable) ──
    const wheelAccum = useRef(0);
    const wheelCooldown = useRef(false);

    useEffect(() => {
        const handler = (e) => {
            if (showCommentsRef.current) return;
            if (e.target.closest(".comments-sheet")) return;
            e.preventDefault();

            // If cooldown is active, hard-block everything — accumulator stays 0
            if (wheelCooldown.current) return;

            wheelAccum.current += e.deltaY;
            if (Math.abs(wheelAccum.current) < 80) return;

            // Determine direction, then immediately reset accum AND lock cooldown
            // BEFORE calling goNext/goPrev — eliminates the race window
            const dir = wheelAccum.current > 0 ? "down" : "up";
            wheelAccum.current = 0;
            wheelCooldown.current = true;
            setTimeout(() => {
                wheelAccum.current = 0; // paranoid reset after cooldown
                wheelCooldown.current = false;
            }, 500);

            if (dir === "down") goNext();
            else goPrev();
        };
        window.addEventListener("wheel", handler, { passive: false });
        return () => window.removeEventListener("wheel", handler);
    }, []);

    // ── Touch swipe — registered via addEventListener (not React props)
    // so we can call preventDefault and stop the browser from also firing wheel ──
    useEffect(() => {
        let startY = null;

        const onStart = (e) => {
            if (isAnimating.current) return;
            startY = e.touches[0].clientY;
        };

        const onEnd = (e) => {
            if (startY === null) return;
            if (isAnimating.current) { startY = null; return; }
            const dy = startY - e.changedTouches[0].clientY;
            startY = null;
            if (Math.abs(dy) < 55) return;
            e.preventDefault();
            if (dy > 0) goNext();
            else goPrev();
        };

        const el = containerRef.current;
        if (!el) return;
        el.addEventListener("touchstart", onStart, { passive: true });
        el.addEventListener("touchend", onEnd, { passive: false });
        return () => {
            el.removeEventListener("touchstart", onStart);
            el.removeEventListener("touchend", onEnd);
        };
    }, []); // stable — goNext/goPrev read from refs

    // ─── LIKE ─────────────────────────────────────────────────────────────────
    const toggleLike = async () => {
        if (!user) return;
        const post = posts[index];
        if (!post) return;
        const liked = post.isLiked;
        setPosts(prev => prev.map((p, i) =>
            i === index ? { ...p, isLiked: !liked, likesCount: liked ? p.likesCount - 1 : p.likesCount + 1 } : p
        ));
        if (liked) {
            await supabase.from("likes").delete().eq("user_id", user.id).eq("post_id", post.id);
        } else {
            await supabase.from("likes").insert({ user_id: user.id, post_id: post.id });
        }
    };

    // ─── BOOKMARK ─────────────────────────────────────────────────────────────
    const toggleBookmark = async () => {
        if (!user) return;
        const post = posts[index];
        if (!post) return;
        const saved = !!bookmarks[post.id];

        // Optimistic update
        setBookmarks(prev => ({ ...prev, [post.id]: !saved }));

        if (saved) {
            await supabase.from("bookmarks")
                .delete()
                .eq("user_id", user.id)
                .eq("post_id", post.id);
        } else {
            await supabase.from("bookmarks")
                .insert({ user_id: user.id, post_id: post.id });
        }
    };

    // ─── COMMENTS ─────────────────────────────────────────────────────────────
    const fetchComments = useCallback(async (postId) => {
        setCommentsLoading(true);
        const { data: commentsData, error } = await supabase
            .from("comments")
            .select("id, content, created_at, user_id")
            .eq("post_id", postId)
            .order("created_at", { ascending: true });

        if (error) { setComments([]); setCommentsLoading(false); return; }

        const userIds = [...new Set(commentsData.map(c => c.user_id))];
        let profileMap = {};
        if (userIds.length > 0) {
            const { data: profilesData } = await supabase
                .from("profiles").select("id, username, avatar_url").in("id", userIds);
            (profilesData || []).forEach(p => { profileMap[p.id] = p; });
        }
        setComments(commentsData.map(c => ({ ...c, profile: profileMap[c.user_id] || null })));
        setCommentsLoading(false);
    }, []);

    const openComments = () => {
        const post = posts[index];
        if (!post) return;
        setComments([]);
        setShowComments(true);
        fetchComments(post.id);
    };

    const submitComment = async () => {
        if (!commentText.trim() || !user || submittingComment) return;
        const post = posts[index];
        setSubmittingComment(true);
        const text = commentText.trim();
        setCommentText("");

        const { error } = await supabase
            .from("comments")
            .insert({ post_id: post.id, user_id: user.id, content: text });

        if (!error) {
            setComments(prev => [...prev, {
                id: `opt-${Date.now()}`,
                content: text,
                created_at: new Date().toISOString(),
                user_id: user.id,
                profile: { username: profile?.username, avatar_url: profile?.avatar_url },
            }]);
            // bump count
            setCommentCounts(prev => ({ ...prev, [post.id]: (prev[post.id] || 0) + 1 }));
        }
        setSubmittingComment(false);
    };

    // ─── DOTS ─────────────────────────────────────────────────────────────────
    const handleCopyLink = () => {
        const post = posts[index];
        if (!post) return;
        navigator.clipboard.writeText(`${window.location.origin}/posts/${post.id}`).then(() => {
            setCopiedMsg(true);
            setTimeout(() => setCopiedMsg(false), 2500);
        });
        setShowDots(false);
    };

    const handleGoToPost = () => {
        const post = posts[index];
        if (!post) return;
        navigate(`/user/${encodeURIComponent(post.profiles?.username)}`);
        setShowDots(false);
    };

    const handleAboutAccount = async () => {
        setShowDots(false);
        const post = posts[index];
        if (!post?.profiles?.id) return;

        // Fetch full profile including country and created_at
        const { data, error } = await supabase
            .from("profiles")
            .select("id, username, avatar_url, country, created_at")
            .eq("id", post.profiles.id)
            .single();

        if (!error && data) {
            setFullAboutProfile(data);
            setShowAboutAccount(true);
        }
    };

    // ─── RENDER ───────────────────────────────────────────────────────────────
    const currentPost = posts[index];
    const slotAPost = posts[slots.a];
    const slotBPost = slots.b !== null ? posts[slots.b] : null;

    return (
        <div className="reels-layout">
            <Sidebar />

            <div
                className="reels-stage"
                ref={containerRef}
            >
                {/* POST CARD AREA */}
                <div className="reel-stack">

                    {/* SLOT A — outgoing or resting */}
                    {slotAPost ? (
                        <div
                            key={`a-${slots.a}`}
                            className={
                                slots.dir === "up"   ? "reel-slide exit-up"
                                : slots.dir === "down" ? "reel-slide exit-down"
                                : "reel-slide at-center"
                            }
                        >
                            <ReelCard post={slotAPost} />
                        </div>
                    ) : (
                        <div className="reel-slide at-center"><SkeletonCard /></div>
                    )}

                    {/* SLOT B — incoming, only during animation */}
                    {slotBPost && slots.dir && (
                        <div
                            key={`b-${slots.b}`}
                            className={slots.dir === "up" ? "reel-slide enter-from-bottom" : "reel-slide enter-from-top"}
                        >
                            <ReelCard post={slotBPost} />
                        </div>
                    )}

                    {/* Fetching skeleton peek */}
                    {!slots.dir && isFetching && index >= posts.length - 1 && (
                        <div className="reel-slide below-peek"><SkeletonCard /></div>
                    )}
                </div>

                {/* RIGHT PANEL — arrows + actions */}
                <div className="reel-right-panel">
                    <button className={`reel-arrow ${index === 0 ? "disabled" : ""}`} onClick={goPrev} aria-label="Previous">
                        <FontAwesomeIcon icon={faChevronUp} />
                    </button>
                    <button className={`reel-arrow ${!hasMore && index >= posts.length - 1 ? "disabled" : ""}`} onClick={goNext} aria-label="Next">
                        <FontAwesomeIcon icon={faChevronDown} />
                    </button>

                    <div className="reel-panel-divider" />

                    {/* Like */}
                    <div className="reel-action-btn" onClick={toggleLike}>
                        <FontAwesomeIcon
                            icon={currentPost?.isLiked ? faHeartSolid : faHeartRegular}
                            className={`action-icon ${currentPost?.isLiked ? "liked" : ""}`}
                        />
                        {currentPost && !currentPost.hide_like_counts && currentPost.likesCount > 0 && (
                            <span className="action-count">{currentPost.likesCount}</span>
                        )}
                    </div>

                    {/* Comment */}
                    {(!currentPost || !currentPost.turn_off_commenting) && (
                        <div className="reel-action-btn" onClick={openComments}>
                            <FontAwesomeIcon icon={faComment} className="action-icon" />
                            {currentPost && commentCounts[currentPost.id] > 0 && (
                                <span className="action-count">{commentCounts[currentPost.id]}</span>
                            )}
                        </div>
                    )}

                    {/* Share */}
                    <div className="reel-action-btn" onClick={() => currentPost && setShowShare(true)}>
                        <FontAwesomeIcon icon={faPaperPlane} className="action-icon" />
                    </div>

                    {/* Bookmark */}
                    <div
                        className="reel-action-btn"
                        onClick={toggleBookmark}
                    >
                        <FontAwesomeIcon
                            icon={currentPost && bookmarks[currentPost.id] ? faBookmarkSolid : faBookmark}
                            className={`action-icon ${currentPost && bookmarks[currentPost.id] ? "bookmarked" : ""}`}
                        />
                    </div>

                    {/* Three dots */}
                    <div className="reel-action-btn" onClick={() => setShowDots(true)}>
                        <FontAwesomeIcon icon={faEllipsisVertical} className="action-icon" />
                    </div>
                </div>

                {/* COMMENTS OVERLAY */}
                {showComments && currentPost && (
                    <CommentsOverlay
                        comments={comments}
                        loading={commentsLoading}
                        commentText={commentText}
                        setCommentText={setCommentText}
                        onSubmit={submitComment}
                        submitting={submittingComment}
                        onClose={() => { setShowComments(false); setCommentText(""); }}
                        profile={profile}
                        inputRef={commentInputRef}
                        navigate={navigate}
                    />
                )}

                {/* DOTS MENU */}
                {showDots && currentPost && (
                    <div className="modal-bg" onClick={() => setShowDots(false)}>
                        <div className="dots-sheet" onClick={e => e.stopPropagation()}>
                            <div className="dots-item" onClick={handleGoToPost}>Go to post</div>
                            <div className="dots-divider" />
                            {/* <div className="dots-item muted">Share to</div> */}
                            <div className="dots-divider" />
                            <div className="dots-item" onClick={handleCopyLink}>Copy link</div>
                            <div className="dots-divider" />
                            <div className="dots-item" onClick={handleAboutAccount}>
                                About this account
                            </div>
                            <div className="dots-divider" />
                            <div className="dots-item cancel" onClick={() => setShowDots(false)}>Cancel</div>
                        </div>
                    </div>
                )}

                {/* ABOUT ACCOUNT MODAL */}
                {fullAboutProfile && (
                    <AboutAccountModal
                        open={showAboutAccount}
                        onClose={() => { setShowAboutAccount(false); setFullAboutProfile(null); }}
                        profile={fullAboutProfile}
                        avatarUrl={fullAboutProfile.avatar_url}
                    />
                )}

                {/* SHARE MODAL */}
                <SharePostModal
                    open={showShare}
                    onClose={() => setShowShare(false)}
                    post={currentPost}
                />

                {/* COPY TOAST */}
                {copiedMsg && <div className="copy-toast">Link copied!</div>}
            </div>
        </div>
    );
}

// ─── REEL CARD ────────────────────────────────────────────────────────────────
function ReelCard({ post }) {
    const navigate = useNavigate();
    const [imgLoaded, setImgLoaded] = useState(false);
    const imageUrl = post?.post_images?.[0]?.signedUrl;

    return (
        <div className="reel-card">
            {!imgLoaded && <SkeletonCard />}
            {imageUrl && (
                <img
                    src={imageUrl}
                    className={`reel-image ${imgLoaded ? "loaded" : "hidden"}`}
                    alt={post.caption || ""}
                    onLoad={() => setImgLoaded(true)}
                />
            )}
            <div className="reel-overlay">
                <div
                    className="reel-user-row"
                    onClick={() => post.profiles?.username && navigate(`/user/${encodeURIComponent(post.profiles.username)}`)}
                >
                    {post.profiles?.avatar_url ? (
                        <img src={post.profiles.avatar_url} className="reel-avatar" alt="" />
                    ) : (
                        <div className="reel-avatar-fallback">
                            <FontAwesomeIcon icon={faUser} />
                        </div>
                    )}
                    <span className="reel-username">{post.profiles?.username}</span>
                </div>
                {post.caption && <p className="reel-caption">{post.caption}</p>}
            </div>
        </div>
    );
}

// ─── SKELETON ─────────────────────────────────────────────────────────────────
function SkeletonCard() {
    return <div className="reel-skeleton" />;
}

// ─── COMMENTS OVERLAY ─────────────────────────────────────────────────────────
function CommentsOverlay({ comments, loading, commentText, setCommentText, onSubmit, submitting, onClose, profile, inputRef, navigate }) {
    const listRef = useRef(null);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [comments]);

    return (
        <div className="comments-backdrop" onClick={onClose}>
            <div className="comments-sheet" onClick={e => e.stopPropagation()}>
                <div className="comments-header">
                    <span className="comments-title">Comments</span>
                    <button className="comments-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="comments-list" ref={listRef}>
                    {loading ? (
                        <div className="comments-loading">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="comment-skeleton-row">
                                    <div className="comment-skel-avatar" />
                                    <div className="comment-skel-lines">
                                        <div className="comment-skel-name" />
                                        <div className="comment-skel-text" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : comments.length === 0 ? (
                        <div className="no-comments">No comments yet. Be the first!</div>
                    ) : (
                        comments.map(c => (
                            <div key={c.id} className="comment-row">
                                <div
                                    className="comment-avatar-wrap"
                                    onClick={() => c.profile?.username && navigate(`/user/${encodeURIComponent(c.profile.username)}`)}
                                >
                                    {c.profile?.avatar_url ? (
                                        <img src={c.profile.avatar_url} className="comment-avatar" alt="" />
                                    ) : (
                                        <div className="comment-avatar-fallback">
                                            <FontAwesomeIcon icon={faUser} />
                                        </div>
                                    )}
                                </div>
                                <div className="comment-body">
                                    <div className="comment-meta">
                                        <span
                                            className="comment-username"
                                            onClick={() => c.profile?.username && navigate(`/user/${encodeURIComponent(c.profile.username)}`)}
                                        >
                                            {c.profile?.username}
                                        </span>
                                        <span className="comment-time">{timeAgo(c.created_at)}</span>
                                    </div>
                                    <p className="comment-text">{c.content}</p>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="comments-input-row">
                    {profile?.avatar_url ? (
                        <img src={profile.avatar_url} className="comments-my-avatar" alt="" />
                    ) : (
                        <div className="comments-my-avatar-fallback">
                            <FontAwesomeIcon icon={faUser} />
                        </div>
                    )}
                    <div className="comments-input-wrap">
                        <input
                            ref={inputRef}
                            className="comments-input"
                            placeholder="Add a comment…"
                            value={commentText}
                            onChange={e => setCommentText(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSubmit(); } }}
                        />
                        {commentText.trim() && (
                            <button className="comments-send" onClick={onSubmit} disabled={submitting}>
                                <FontAwesomeIcon icon={faPaperPlaneSolid} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
